import { useTranslation } from 'react-i18next';
import { useEffect, useId, useState } from 'react';

import { Modal } from '../../components';
import { useQltClassNoteStore } from '../../stores/qlt';
import { modalMeta } from '../periodLabels';
import { EMPTY_CHECKLIST, toggleItem, type ChecklistText } from './checklistText';
import { CLASS_COMMENT_RULE, CLASS_MARK_LOW_SCORES, CLASS_MARK_SCORES, commentForMark, commentPayload, commentPresetsFor, hydrateCommentText, markOf, markOnOtherPage, restoreForMark } from './classNoteFields';
import { useMsg } from '../../i18n/msg';
import '../../styles/class-management.css';

// the box holds the generated sentence as well as the teacher's words
const TEXT_LIMIT = 400;

export { CLASS_COMMENT_PRESETS, classMarkLabel } from './classNoteFields';

export interface ClassLogbookEditorProps {
  open: boolean;
  periodLabel: string;
  classTitle?: string;
  subjectTitle?: string;
  onClose: () => void;
  onSaved: () => void;
}

export function ClassLogbookEditor({ open, periodLabel, classTitle, subjectTitle, onClose, onSaved }: ClassLogbookEditorProps) {
  const { t } = useTranslation(['panels', 'common']);
  const tr = useMsg();
  const note = useQltClassNoteStore((state) => state.currentNote);
  const loading = useQltClassNoteStore((state) => state.loading);
  const error = useQltClassNoteStore((state) => state.error);
  const saveData = useQltClassNoteStore((state) => state.saveData);
  const [mark, setMark] = useState<number | null>(null);
  // the score row is one row of two pages: 10..5, then 4..0
  const [lowPage, setLowPage] = useState(false);
  const [comment, setComment] = useState<ChecklistText>(EMPTY_CHECKLIST);
  const scoresId = useId();

  useEffect(() => {
    if (!open) return;
    const stored = markOf(note?.data);
    setMark(stored);
    setLowPage(stored !== null && stored < 5);
    // a legacy tick with no mark, or one a QLT web mark edit hides, keeps its words as typed text
    setComment(restoreForMark(hydrateCommentText(note?.data), stored));
  }, [note, open]);

  function pickMark(next: number | null) {
    setMark(next);
    setComment((current) => commentForMark(current, next));
  }

  function toggleTag(value: string) {
    setComment((current) => toggleItem(current, CLASS_COMMENT_RULE, value));
  }

  async function save() {
    // undefined drops the retired classMarkBand so a stale band never outlives a cleared mark
    const updated = await saveData({ classMark: mark === null ? undefined : mark, classMarkBand: undefined, ...commentPayload(comment.ticks, comment.text) });
    if (updated) {
      onSaved();
      onClose();
    }
  }

  const options = commentPresetsFor(mark);
  const hiddenPick = markOnOtherPage(mark, lowPage);
  const pagerLabel = `${lowPage ? t('logbook.pager.high') : t('logbook.pager.low')}${hiddenPick === null ? '' : t('logbook.pager.picked', { score: hiddenPick })}`;
  const description = modalMeta({ classTitle, periodLabel, subjectTitle });
  const actions = <button type="button" className="cm-btn-primary" disabled={loading || !note || mark === null} onClick={() => void save()}>{loading ? t('common:states.saving') : t('logbook.confirm')}</button>;

  return (
    <Modal open={open} title={t('logbook.title')} description={description} className="cm-modal cm-modal--meta cm-modal--fill" layerClassName="cm-layer--fill" actions={actions} onClose={onClose}>
      <div className="cm-label">{t('logbook.mark')}</div>
      <div className="cm-eval-row">
        <div className="cm-eval-grid" id={scoresId} role="radiogroup" aria-label={t('logbook.mark')}>
          {(lowPage ? CLASS_MARK_LOW_SCORES : CLASS_MARK_SCORES).map((score) => (
            <button
              key={score}
              type="button"
              role="radio"
              aria-checked={mark === score}
              className={mark === score ? 'cm-eval-chip is-on' : 'cm-eval-chip'}
              onClick={() => pickMark(mark === score ? null : score)}
            >
              {score}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`cm-eval-more${lowPage ? ' is-low' : ''}`}
          aria-controls={scoresId}
          aria-label={pagerLabel}
          onClick={() => setLowPage(!lowPage)}
        >
          <svg width="14" height="9" viewBox="0 0 14 9" aria-hidden={true}><path d="M1.5 1.5 7 7l5.5-5.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          {hiddenPick === null ? null : <span className="cm-eval-more__badge" aria-hidden={true}>{hiddenPick}</span>}
        </button>
      </div>
      <div className="cm-label">{t('logbook.commentLabel')}</div>
      {mark === null ? <p className="cm-checklist-hint">{t('logbook.pickMarkHint')}</p> : (
        <div className="cm-checklist cm-checklist--cols" role="group" aria-label={t('logbook.commentLabel')}>
          {options.map((item) => (
            <label className={comment.ticks.indexOf(item) >= 0 ? 'cm-check is-on' : 'cm-check'} key={item}>
              <input type="checkbox" checked={comment.ticks.indexOf(item) >= 0} onChange={() => toggleTag(item)} />
              <span>{item}</span>
            </label>
          ))}
        </div>
      )}
      <label className="cm-label" htmlFor="class-comment-text">{t('entry.text')}</label>
      <div className="cm-note">
        <textarea id="class-comment-text" value={comment.text} maxLength={TEXT_LIMIT} onChange={(event) => setComment({ ...comment, text: event.target.value })} placeholder={t('logbook.textPlaceholder')} />
        <span className="cm-note__count">{t('entry.count', { length: comment.text.length, limit: TEXT_LIMIT })}</span>
      </div>
      {error ? <p className="cm-error" role="alert">{tr(error)}</p> : null}
    </Modal>
  );
}
